import React, { useEffect } from 'react';
import { useAtom } from 'jotai';
import { createTheme, ThemeProvider } from '@mui/material';
import LzPAppBootstrap from './type';
import LzXAppBootstrapConclude from './conclude';
import lzSLayoutThemePaletteModeAtom from '@lz-atoms/Layout/themePaletteMode';
import lzSLayoutThemeDirectionAtom from '@lz-atoms/Layout/themeDirection';

export default function LzXAppBootstrapThemeSetup(props: LzPAppBootstrap) {
    // Store state getters and setters
    const [layoutThemeDirectionState, setLayoutThemeDirectionState] = useAtom(lzSLayoutThemeDirectionAtom);
    const [layoutThemePaletteModeState, setLayoutThemePaletteModeState] = useAtom(lzSLayoutThemePaletteModeAtom);

    // Run on component mount (only once)
    useEffect(() => {
        // If value provided is different than the store's initial value then change it
        if (props.theme?.direction && layoutThemeDirectionState !== props.theme.direction) {
            setLayoutThemeDirectionState(props.theme.direction);
        }
        if (props.theme?.palette?.mode && layoutThemePaletteModeState !== props.theme.palette.mode) {
            setLayoutThemePaletteModeState(props.theme.palette.mode);
        }
    }, []);

    // Build a theme to get started with
    const theme = createTheme(props.theme);

    // Update it with the current layout state data (options that are changeable)
    theme.direction = layoutThemeDirectionState;
    theme.palette.mode = layoutThemePaletteModeState;

    // Load the css variables with the themes data
    document.documentElement.style.cssText = `
        --primary-color: ${theme.palette.primary.main};
        --primary-light-color: ${theme.palette.primary.light};
        --primary-dark-color: ${theme.palette.primary.dark};
        --primary-contrastText-color: ${theme.palette.primary.contrastText};
        
        --secondary-color: ${theme.palette.secondary.main};
        --secondary-light-color: ${theme.palette.secondary.light};
        --secondary-dark-color: ${theme.palette.secondary.dark};
        --secondary-contrastText-color: ${theme.palette.secondary.contrastText};

        --error-color: ${theme.palette.error.main};
        --error-light-color: ${theme.palette.error.light};
        --error-dark-color: ${theme.palette.error.dark};

        --warning-color: ${theme.palette.warning.main};
        --warning-light-color: ${theme.palette.warning.light};
        --warning-dark-color: ${theme.palette.warning.dark};

        --info-color: ${theme.palette.info.main};
        --info-light-color: ${theme.palette.info.light};
        --info-dark-color: ${theme.palette.info.dark};

        --success-color: ${theme.palette.success.main};
        --success-light-color: ${theme.palette.success.light};
        --success-dark-color: ${theme.palette.success.dark};
        
        --text-primary-color: ${theme.palette.text.primary};
        --text-secondary-color: ${theme.palette.text.secondary};
        --text-on-dark-color: white;
        --text-disabled-color: ${theme.palette.text.secondary};
        --body-background-color: ${theme.palette.background};
        --paper-background-color: ${theme.palette.background};
        --grey-light-color: ${theme.palette.grey[500]};
        --grey-lighter-color: ${theme.palette.grey[400]};
        --grey-lightest-color: ${theme.palette.grey[300]};
        --grey-color: ${theme.palette.grey[600]};
        --grey-dark-color: ${theme.palette.grey[700]};
        --grey-darker-color: ${theme.palette.grey[800]};
        --grey-darkest-color: ${theme.palette.grey[900]};
        --primary-font: ${theme.typography.fontFamily};
        --nav-width: 340px;
        --nav-padding: 12px;
        --nav-width-less-padding: calc(340px - 12px - 12px);
    `;

    // Render the next level
    return (
        <React.Fragment>
            <ThemeProvider theme={theme}>
                <LzXAppBootstrapConclude {...props} />
            </ThemeProvider>
        </React.Fragment>
    );
}
